import { useCallback, useEffect } from 'react';

import { convertsCentsToCurrency } from '@npi/buddy-utils';
import { useField } from 'formik';
import { BiMinus } from 'react-icons/bi';

import { getVatAmount } from 'src/lib/countryConfig';

import { Flex } from '../Flex';
import { DropDown, FieldInput } from '../FormikComponents';
import { OptionTypes } from '../FormikComponents/DropDown';
import NPICurrencyInputLabelled from '../inputs/NPICurrencyInput/NPICurrencyInputLabelled';

const INVOICE_LINE_ITEM_FIELD_NAMES = {
    Date: 'date',
    LEDGER_ACCOUNT: 'ledgerAccount',
    DESCRIPTION: 'description',
    QUANTITY: 'quantity',
    NET_AMOUNT: 'netAmount',
    TOTAL_NET: 'totalNet',
    TOTAL_VAT: 'totalVat',
    LINE_TOTAL: 'lineTotal',
};

interface InvoiceLineItemProps {
    index: number;
    formNamePrefix: string;
    ledgerAccounts: OptionTypes[];
    removeItem: (index: number) => void;
}

const InvoiceLineItem = ({
    formNamePrefix,
    index,
    ledgerAccounts,
    removeItem,
}: InvoiceLineItemProps) => {
    const name = `${formNamePrefix}.${index}`;

    const [{ value: quantity }] = useField(`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.QUANTITY}`);
    const [{ value: netAmount }, _, { setValue: setNetAmount }] = useField(
        `${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.NET_AMOUNT}`
    );
    const [{ value: totalNet }, __, { setValue: setTotalNet }] = useField(
        `${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.TOTAL_NET}`
    );
    const [{ value: totalVat }, ____, { setValue: setTotalVat }] = useField(
        `${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.TOTAL_VAT}`
    );
    const [{ value: lineTotal }, ______, { setValue: setLineTotal }] = useField(
        `${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.LINE_TOTAL}`
    );

    useEffect(() => {
        const qty = Number(quantity);
        const net = Number(netAmount);

        if (Number.isFinite(qty) && Number.isFinite(net)) {
            const netCents = qty * net;
            const vatCents = netCents * getVatAmount();
            const lineCents = netCents + vatCents;

            setTotalVat(Math.round(vatCents));
            setTotalNet(Math.round(netCents));
            setLineTotal(Math.round(lineCents));
        }
        // else: do not update, leave previous values
    }, [quantity, netAmount, setTotalVat, setTotalNet, setLineTotal]);

    const stableSetNetAmount = useCallback(
        (valueInCents) => {
            if (Number.isFinite(valueInCents)) {
                setNetAmount(valueInCents);
            }
        },
        [setNetAmount]
    );

    return (
        <Flex gap="16px" alignItems="flex-start">
            <DropDown
                label="Ledger Account"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.LEDGER_ACCOUNT}`}
                options={ledgerAccounts}
            />
            <FieldInput
                label="Description"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.DESCRIPTION}`}
            />
            <FieldInput
                label="Quantity"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.QUANTITY}`}
                type="number"
                allowDecimal={false}
                minVal={0}
                maxVal={parseFloat(INVOICE_LINE_ITEM_FIELD_NAMES.QUANTITY)}
            />

            <NPICurrencyInputLabelled
                label="Net Amount"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.NET_AMOUNT}`}
                defaultValueInCents={netAmount}
                handleValue={stableSetNetAmount}
            />

            <NPICurrencyInputLabelled
                label="Total Net"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.TOTAL_NET}`}
                disabled
                value={convertsCentsToCurrency(Math.round(totalNet))}
            />

            <NPICurrencyInputLabelled
                label="Total Vat"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.TOTAL_VAT}`}
                disabled
                value={convertsCentsToCurrency(Math.round(totalVat))}
            />

            <NPICurrencyInputLabelled
                label="Line Total"
                name={`${name}.${INVOICE_LINE_ITEM_FIELD_NAMES.LINE_TOTAL}`}
                disabled
                value={convertsCentsToCurrency(Math.round(lineTotal))}
            />

            <Flex
                padding="6px"
                margin="auto"
                sx={{ cursor: 'pointer' }}
                bgcolor="black"
                color="white"
                borderRadius="4px"
                visibility={index === 0 ? 'hidden' : 'visible'}
                onClick={() => {
                    return removeItem(index);
                }}
            >
                <BiMinus size="14px" />
            </Flex>
        </Flex>
    );
};

export default InvoiceLineItem;
